import {
  FinanceAccountType,
  FinanceCategoryScope,
  type CreateFinanceAccountInput,
  type FinanceAccountRecord,
  type ListFinanceAccountsQuery,
  type UpdateFinanceAccountInput,
} from "@/features/finance/model/finance-types"
import {
  http,
} from "@/shared/lib/api/http"
import {
  Currency,
} from "@ibada/shared-types/finance"
import {
  normalizeBoolean,
  normalizeNullableMoney,
  normalizeNullableString,
  normalizeNumber,
  normalizeString,
} from "./finance-api-shared"


function normalizeFinanceAccountRecord(value: unknown): FinanceAccountRecord {
  const record = (value ?? {}) as Record<string, unknown>
  const branchRecord = (record.branch ?? {}) as Record<string, unknown>

  return {
    id: normalizeString(record.id) ?? "",
    workspaceId: normalizeString(record.workspaceId ?? record.workspace_id) ?? "",
    branchId: normalizeString(record.branchId ?? record.branch_id) ?? "",
    name: normalizeString(record.name) ?? "",
    description: normalizeNullableString(record.description),
    accountNumberLast4: normalizeNullableString(record.accountNumberLast4 ?? record.account_number_last4),
    // IBA-114 — how the money is held, and what it is held in. The account is the source of truth for its own
    // currency; nothing here reads a workspace-wide setting any more.
    type: (normalizeString(record.type) as FinanceAccountType) ?? FinanceAccountType.Bank,
    currency: (normalizeString(record.currency) as Currency) ?? Currency.USD,
    status: normalizeString(record.status) === "ARCHIVED" ? "ARCHIVED" : "ACTIVE",
    scope: record.scope === FinanceCategoryScope.Parish ? FinanceCategoryScope.Parish : FinanceCategoryScope.Branch,
    balance: normalizeNullableMoney(record.balance),
    openingBalance: normalizeNullableMoney(record.openingBalance ?? record.opening_balance),
    openingBalanceLocked: normalizeBoolean(record.openingBalanceLocked ?? record.opening_balance_locked),
    accountingAccountId: normalizeNullableString(record.accountingAccountId ?? record.accounting_account_id),
    accountingAccountCode: normalizeNullableString(record.accountingAccountCode ?? record.accounting_account_code),
    hasLedgerActivity: normalizeBoolean(record.hasLedgerActivity ?? record.has_ledger_activity),
    canEditFinancialDetails: typeof (record.canEditFinancialDetails ?? record.can_edit_financial_details) === "boolean"
      ? normalizeBoolean(record.canEditFinancialDetails ?? record.can_edit_financial_details)
      : true,
    branch: {
      id: normalizeString(branchRecord.id) ?? "",
      workspaceId: normalizeString(branchRecord.workspaceId ?? branchRecord.workspace_id) ?? "",
      name: normalizeString(branchRecord.name) ?? "",
      slug: normalizeString(branchRecord.slug) ?? "",
      status: normalizeString(branchRecord.status) ?? "ACTIVE",
    },
    createdAt: normalizeString(record.createdAt ?? record.created_at) ?? new Date().toISOString(),
    updatedAt: normalizeString(record.updatedAt ?? record.updated_at) ?? new Date().toISOString(),
  }
}


export async function listFinanceAccounts(query: ListFinanceAccountsQuery): Promise<{
  items: FinanceAccountRecord[]
  total: number
  page: number
  limit: number
}> {
  const response = await http.get<unknown>("/finance/accounts", {
    params: {
      workspaceId: query.workspaceId,
      ...(typeof query.branchId !== "undefined" ? { branchId: query.branchId } : {}),
      ...(query.departmentId ? { departmentId: query.departmentId } : {}),
      ...(query.scope ? { scope: query.scope } : {}),
      ...(query.status ? { status: query.status } : {}),
      ...(query.includeBalances ? { includeBalances: true } : {}),
      ...(query.search ? { search: query.search } : {}),
      ...(typeof query.page === "number" ? { page: query.page } : {}),
      ...(typeof query.limit === "number" ? { limit: Math.min(query.limit, 100) } : {}),
      ...(query.sortBy ? { sortBy: query.sortBy } : {}),
      ...(query.sortOrder ? { sortOrder: query.sortOrder } : {}),
    },
  })

  const record = (response.data ?? {}) as Record<string, unknown>
  const items = Array.isArray(record.items) ? record.items.map(normalizeFinanceAccountRecord) : []

  return {
    items,
    total: normalizeNumber(record.total) ?? items.length,
    page: normalizeNumber(record.page) ?? 1,
    limit: normalizeNumber(record.limit) ?? Math.max(items.length, 1),
  }
}


/**
 * The supported account HOLDINGS ("held as"), served from the backend's live `FinanceAccountType` enum so the
 * dropdown can never drift from what the API actually accepts. The backend owns the value set; the UI localises
 * each value via the `accountTypes.*` messages.
 */
export async function fetchFinanceAccountTypes(): Promise<string[]> {
  const response = await http.get<{ types?: string[] }>("/finance/accounts/types")
  return Array.isArray(response.data?.types) ? response.data.types : []
}


export async function createFinanceAccount(input: CreateFinanceAccountInput): Promise<FinanceAccountRecord> {
  const response = await http.post<unknown>("/finance/accounts", {
    workspaceId: input.workspaceId,
    ...(typeof input.branchId !== "undefined" ? { branchId: input.branchId } : {}),
    ...(input.departmentId ? { departmentId: input.departmentId } : {}),
    ...(input.scope ? { scope: input.scope } : {}),
    name: input.name,
    description: input.description ?? null,
    ...(typeof input.accountNumberLast4 !== "undefined" ? { accountNumberLast4: input.accountNumberLast4 } : {}),
    ...(typeof input.type !== "undefined" ? { type: input.type } : {}),
    // A canonical decimal string, sent through untouched (IBA-116). The `typeof … === "number"` guard this
    // replaces would now DROP every opening balance on the floor — a silently unset balance, no error anywhere.
    ...(typeof input.openingBalance === "string" ? { openingBalance: input.openingBalance } : {}),
  })

  return normalizeFinanceAccountRecord(response.data)
}


export async function updateFinanceAccount(input: UpdateFinanceAccountInput): Promise<FinanceAccountRecord> {
  const response = await http.patch<unknown>(`/finance/accounts/${input.id}`, {
    ...input.patch,
    ...(typeof input.patch.type !== "undefined" ? { type: input.patch.type } : {}),
  })
  return normalizeFinanceAccountRecord(response.data)
}


export async function deactivateFinanceAccount(id: string): Promise<{ id: string; status: "ACTIVE" | "ARCHIVED" }> {
  const response = await http.post<unknown>(`/finance/accounts/${id}/deactivate`)
  const record = (response.data ?? {}) as Record<string, unknown>
  return {
    id: normalizeString(record.id) ?? id,
    status: normalizeString(record.status) === "ARCHIVED" ? "ARCHIVED" : "ACTIVE",
  }
}


export async function activateFinanceAccount(id: string): Promise<{ id: string; status: "ACTIVE" | "ARCHIVED" }> {
  const response = await http.post<unknown>(`/finance/accounts/${id}/activate`)
  const record = (response.data ?? {}) as Record<string, unknown>
  return {
    id: normalizeString(record.id) ?? id,
    status: normalizeString(record.status) === "ARCHIVED" ? "ARCHIVED" : "ACTIVE",
  }
}
